"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.prepareUpload = exports.getFiles = exports.getPendingFiles = exports.sendMessage = exports.getMessage = void 0;
const t = require("io-ts");
const fp = require("fp-ts");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const ActionTypes = require("../../actionTypes/bid/message");
const Actions = require("../../actions/bid/message");
const sok_types_1 = require("@caddigital/sok-types");
const validation_1 = require("../../utils/validation");
const errorHandling_1 = require("../../utils/errorHandling");
function getMessage(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_REQUEST), (0, operators_1.mergeMap)((action) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/bid/${action.payload.bid.id}/message/${action.payload.message}`
    }).pipe((0, operators_1.mergeMap)((xhr) => {
        const validation = sok_types_1.Mail.iMail.decode(xhr.response);
        const act = validation.fold((e) => [Actions.fetchFailed((0, errorHandling_1.parseValidationError)(e))], (bid) => {
            const actions = [Actions.fetchSuccess(bid)];
            if (bid.attachmentFolder !== undefined) {
                actions.push(Actions.fetchFilesRequest(bid.attachmentFolder));
            }
            return actions;
        });
        return (0, rxjs_1.of)(...act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.getMessage = getMessage;
function validateSendMessage(action) {
    const to = action.payload.message.to.split(';');
    const validation = fp.function.compose((0, validation_1.ap)((0, validation_1.isNotEmpty)('to')(action.payload.message)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('subject')(action.payload.message)))((0, validation_1.isNotEmpty)('message')(action.payload.message));
    return to.reduce((acc, email) => email.trim().length > 0
        ? (0, validation_1.ap)((0, validation_1.isEmail)('email')(email).mapFailure((errors) => errors.map((x) => (Object.assign(Object.assign({}, x), { value: `${email}: ${x.value}` })))))(acc)
        : acc, validation);
}
function sendMessage(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.SEND_REQUEST), (0, operators_1.debounceTime)(200), (0, operators_1.mergeMap)((action) => validateSendMessage(action).fold((e) => (0, rxjs_1.of)(Actions.sendValidationFailed(e)), () => (0, rxjs_1.merge)((0, rxjs_1.of)(Actions.sendValidationSuccess()), ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'POST',
        body: Object.assign(Object.assign({}, action.payload.message), { attachments: action.payload.message.attachments.map((x) => x.id) }),
        url: `${baseUrl}api/bid/${action.payload.bid.id}/message`
    }).pipe((0, operators_1.mergeMap)((xhr) => (0, rxjs_1.of)(Actions.sendSuccess())), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.sendFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))))))));
}
exports.sendMessage = sendMessage;
function getPendingFiles(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_PENDING_FILES_REQUEST), (0, operators_1.mergeMap)((action) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'POST',
        body: {
            type: 'SESSION',
            uuids: [action.payload]
        },
        url: `${baseUrl}api/files/metadata`
    }).pipe((0, operators_1.mergeMap)((xhr) => (0, rxjs_1.of)(Actions.fetchPendingFilesSuccess(xhr.response.list))), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchPendingFilesFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.getPendingFiles = getPendingFiles;
function getFiles(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_FILES_REQUEST), (0, operators_1.mergeMap)((action) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/files/${action.payload}?includeSubFolders=true`
    }).pipe((0, operators_1.mergeMap)((xhr) => {
        const validation = t
            .array(sok_types_1.File.iRevisionOrFolder)
            .decode(xhr.response.files);
        const act = validation.fold((e) => Actions.fetchFilesFailed((0, errorHandling_1.parseValidationError)(e)), (files) => {
            return Actions.fetchFilesSuccess(files);
        });
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchFilesFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.getFiles = getFiles;
function prepareUpload(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.PREPARE_UPLOAD_REQUEST), (0, operators_1.mergeMap)((action) => {
        const createdFolder = store.value.bid.message.createdFolder;
        if (createdFolder) {
            return (0, rxjs_1.of)(Actions.prepareUploadSuccess(createdFolder, action.payload.structure));
        }
        return ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'POST',
            url: `${baseUrl}api/files/`,
            body: {
                name: 'bid_message_attachments',
                type: 'Folder',
                parent: action.payload.parent.id,
                description: 'message attachments'
            }
        }).pipe((0, operators_1.mergeMap)(({ response }) => {
            const validation = sok_types_1.File.iFolder.decode(response);
            const act = validation.fold((e) => [Actions.prepareUploadFailed((0, errorHandling_1.parseValidationError)(e))], (res) => [
                Actions.prepareUploadSuccess(res, action.payload.structure)
            ]);
            return (0, rxjs_1.of)(...act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.prepareUploadFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
    }));
}
exports.prepareUpload = prepareUpload;
//# sourceMappingURL=message.js.map